import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent, ReactNode } from "react";
import { Fragment, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { AuthorName } from "~/components/author/author-name";
import type { authorNamesArticle$key } from "~/queries/__generated__/authorNamesArticle.graphql";

import { AuthorLocation as Location } from "./enums";
import { AndSeparator, AuthorLocation, AuthorNamesContainer, CommaSeparator } from "./styles";

export type Props = {
  children?: ReactNode;
  reference: authorNamesArticle$key;
};

export const AuthorNames: FunctionComponent<Props> = ({
  children,
  reference: reference_,
  ...attribs
}) => {
  const { authorLocations, authors } = useFragment(
    graphql`
      fragment authorNamesArticle on Article {
        authorLocations
        authors {
          entityId
          location
          ...authorNameAuthor
        }
      }
    `,
    reference_,
  );

  const renderLocations = useCallback(
    (index: number) => {
      if (!authors) return null;
      const authorsLocation = authors.map((author, index) => {
        const authorLocationInArticle = (authorLocations as OrUndefined<string>[])?.[index];

        // Use article author location when it has value otherwise use author.location
        return !notEmpty(authorLocationInArticle) ||
          authorLocationInArticle?.toLowerCase?.() === Location.NotAvailable
          ? (author?.location as OrUndefined<string>)
          : authorLocationInArticle;
      });
      const isAllAuthorsInHongKongOrEmpty = authorsLocation.every(
        // Check all author locations are Hong Kong or empty
        location => location?.toLowerCase() === Location.HongKong || !notEmpty(location),
      );

      const location = authorsLocation[index];
      if (authors.length > 3 || isAllAuthorsInHongKongOrEmpty || !location) return null;
      return <AuthorLocation>in {location}</AuthorLocation>;
    },
    [authorLocations, authors],
  );

  if (!notEmpty(authors)) return null;

  const renderCommaOrAnd = (index: number) => {
    const lastIndex = authors.length - 1;
    if (authors.length <= 1 || index === lastIndex) return null;
    // If current index is previous index before lastIndex in the array (No need to render and or , at the last item)
    return index === lastIndex - 1 ? (
      <AndSeparator>and</AndSeparator>
    ) : (
      <CommaSeparator>,</CommaSeparator>
    );
  };

  return (
    <AuthorNamesContainer {...attribs}>
      {authors?.map((author, index) => {
        if (!author) return null;

        return (
          <Fragment key={author.entityId}>
            <AuthorName reference={author} />
            {renderLocations(index)}
            {renderCommaOrAnd(index)}
          </Fragment>
        );
      })}
      {children}
    </AuthorNamesContainer>
  );
};

AuthorNames.displayName = "AuthorNames";
